import { motion } from "motion/react";
import cx from "classnames";
import { Button, Checkbox, Divider, Heading, Link, TextField } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import {
  BooleanFilterPair,
  PrimaryActionLabel,
  ProgrammingStructuresFilters as Filters,
  STRUCTURE_SOURCE,
  StructureSourceCode,
} from "@services/types/programming-structures/programmingStructuresFiltersTypes";
import SubjectCodeSelector from "@modules/programming-structures/components/subject-code-selector";
import styles from "./programmingStructuresFilters.module.scss";

interface ProgrammingStructuresFiltersProps {
  filters: Filters;
  scenarioId: string;
  originId: string;
  isAdvancedPanelOpen: boolean;
  isLoading: boolean;
  isPending: boolean;
  hasSelection: boolean;
  primaryActionLabel: PrimaryActionLabel;
  onToggleAdvancedPanel: () => void;
  onSubjectChange: (subject: Filters["subject"]) => void;
  onStructureCodeChange: (structureCode: string) => void;
  onSourceToggle: (code: StructureSourceCode) => void;
  onPublishedToggle: (key: keyof BooleanFilterPair) => void;
  onVisibleToggle: (key: keyof BooleanFilterPair) => void;
  onApply: () => void;
  onClear: () => void;
}

const PRIMARY_ACTION = {
  search: { key: "search" },
  apply: { key: "apply" },
  update: { key: "update" },
} as const;

const panelAnimation = {
  initial: { height: 0, opacity: 0, overflow: "hidden" },
  transition: {
    height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] as const },
    opacity: { duration: 0.3, ease: "easeInOut" as const },
  },
};

const ProgrammingStructuresFilters = ({
  filters,
  scenarioId,
  originId,
  isAdvancedPanelOpen,
  isLoading,
  isPending,
  hasSelection,
  primaryActionLabel,
  onToggleAdvancedPanel,
  onSubjectChange,
  onStructureCodeChange,
  onSourceToggle,
  onPublishedToggle,
  onVisibleToggle,
  onApply,
  onClear,
}: ProgrammingStructuresFiltersProps) => {
  const { t } = useTranslation();
  const primaryAction = PRIMARY_ACTION[primaryActionLabel];

  return (
    <section className={styles.programmingStructuresFilters}>
      <div className={styles.programmingStructuresFilters_topRow}>
        <div className={styles.programmingStructuresFilters_field}>
          <SubjectCodeSelector
            scenarioId={scenarioId}
            originId={originId}
            value={filters.subject}
            isDisabled={isLoading}
            onChange={onSubjectChange}
          />
        </div>

        <div className={styles.programmingStructuresFilters_field}>
          <TextField
            label={t("programming-structures.filters.structure-code.label")}
            placeholder={t("programming-structures.filters.structure-code.placeholder")}
            value={filters.structureCode}
            disabled={isLoading}
            onChange={event => onStructureCodeChange(event?.target?.value ?? "")}
          />
        </div>

        <Link
          className={styles.programmingStructuresFilters_toggle}
          iconRight={isAdvancedPanelOpen ? "chevron-up" : "chevron-down"}
          disabled={isLoading}
          onClick={onToggleAdvancedPanel}
        >
          {isAdvancedPanelOpen
            ? t("programming-structures.filters.less-filters")
            : t("programming-structures.filters.more-filters")}
        </Link>

        <div className={styles.programmingStructuresFilters_actions}>
          {hasSelection && (
            <Button variant="ghost" disabled={isLoading} onClick={onClear}>
              {t("programming-structures.filters.clear")}
            </Button>
          )}

          <Button loading={isLoading} disabled={!isPending || isLoading} onClick={onApply}>
            {t(`programming-structures.filters.actions.${primaryAction.key}`)}
          </Button>
        </div>
      </div>

      <motion.div
        {...panelAnimation}
        animate={
          isAdvancedPanelOpen
            ? { height: "auto", opacity: 1, overflow: "visible" }
            : { height: 0, opacity: 0, overflow: "hidden" }
        }
      >
        <ul
          className={cx(
            styles.programmingStructuresFilters_panel,
            !isAdvancedPanelOpen && styles.programmingStructuresFilters_panel__hidden,
          )}
        >
          <li className={styles.programmingStructuresFilters_group}>
            <Heading type="h4" className={styles.programmingStructuresFilters_groupTitle}>
              {t("programming-structures.filters.source.title")}
            </Heading>
            <div className={styles.programmingStructuresFilters_options}>
              <Checkbox
                labelRight={t("programming-structures.filters.source.base")}
                checked={filters.sources[STRUCTURE_SOURCE.base]}
                disabled={isLoading}
                onChange={() => onSourceToggle(STRUCTURE_SOURCE.base)}
              />
              <Checkbox
                labelRight={t("programming-structures.filters.source.user-defined")}
                checked={filters.sources[STRUCTURE_SOURCE.user]}
                disabled={isLoading}
                onChange={() => onSourceToggle(STRUCTURE_SOURCE.user)}
              />
            </div>
          </li>

          <li className={styles.programmingStructuresFilters_separator}>
            <Divider className={styles.programmingStructuresFilters_divider} vertical />
          </li>

          <li className={styles.programmingStructuresFilters_group}>
            <Heading type="h4" className={styles.programmingStructuresFilters_groupTitle}>
              {t("programming-structures.filters.status.title")}
            </Heading>
            <div className={styles.programmingStructuresFilters_options}>
              <Checkbox
                labelRight={t("programming-structures.filters.status.published")}
                checked={filters.isPublished.yes}
                disabled={isLoading}
                onChange={() => onPublishedToggle("yes")}
              />
              <Checkbox
                labelRight={t("programming-structures.filters.status.not-published")}
                checked={filters.isPublished.no}
                disabled={isLoading}
                onChange={() => onPublishedToggle("no")}
              />
            </div>
          </li>

          <li className={styles.programmingStructuresFilters_separator}>
            <Divider vertical />
          </li>

          <li className={styles.programmingStructuresFilters_group}>
            <Heading type="h4" className={styles.programmingStructuresFilters_groupTitle}>
              {t("programming-structures.filters.visibility.title")}
            </Heading>
            <div className={styles.programmingStructuresFilters_options}>
              <Checkbox
                labelRight={t("programming-structures.filters.visibility.visible")}
                checked={filters.isVisible.yes}
                disabled={isLoading}
                onChange={() => onVisibleToggle("yes")}
              />
              <Checkbox
                labelRight={t("programming-structures.filters.visibility.not-visible")}
                checked={filters.isVisible.no}
                disabled={isLoading}
                onChange={() => onVisibleToggle("no")}
              />
            </div>
          </li>
        </ul>
      </motion.div>
    </section>
  );
};

export default ProgrammingStructuresFilters;
